// src/pages/FlaggedGoGetsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Filter, LayoutGrid, List } from 'lucide-react';
import { reportsService } from '../services/reportsService';

// Import our unified components
import ModerationStats from '../components/reports/ModerationStats';
import ModeratedContentList from '../components/reports/ModeratedContentList';
import ReportReviewModal from '../components/reports/ReportReviewModal';
import FlaggedGoGetCard from '../components/reports/FlaggedGoGetCard';

function FlaggedGoGetsPage() {
  const [selectedGoGetId, setSelectedGoGetId] = useState(null);
  const [stats, setStats] = useState({
    flagged: 0,
    highSeverity: 0,
    resolvedToday: 0,
    automated: 0,
    userReported: 0
  });
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [filter, setFilter] = useState({
    severity: 'all',
    source: 'all',
    status: 'pending'
  });

  const fetchStats = async () => {
    console.log('[FlaggedGoGetsPage] Fetching stats');
    try {
      const data = await reportsService.getStats(24);
      console.log('[FlaggedGoGetsPage] Received stats:', data);
      setStats({
        flagged: data.gogetContent?.flagged || 0,
        highSeverity: data.gogetContent?.highSeverity || 0,
        resolvedToday: data.resolvedFlags || 0,
        automated: data.gogetContent?.automated || 0,
        userReported: data.gogetContent?.userReported || 0
      });
    } catch (error) {
      console.error('[FlaggedGoGetsPage] Error fetching stats:', error);
      setStats({
        flagged: 0,
        highSeverity: 0,
        resolvedToday: 0,
        automated: 0,
        userReported: 0
      });
    }
  };

  const fetchGoGets = useCallback(async () => {
    console.log('[FlaggedGoGetsPage] Fetching flagged GoGets');
    setLoading(true);
    try {
      const result = await reportsService.getFlaggedContentByType({
        contentType: 'goget',
        page: currentPage,
        limit: viewMode === 'grid' ? 9 : 20,
        severity: filter.severity !== 'all' ? filter.severity : undefined,
        source: filter.source !== 'all' ? filter.source : undefined,
        status: filter.status
      });
  
      
      setReports(result.reports || []);
      setTotalPages(result.totalPages || 1);
      console.log('[FlaggedGoGetsPage] Fetched reports:', {
        count: result.reports?.length || 0,
        total: result.total,
        pages: result.totalPages
      });
    } catch (error) {
      console.error('[FlaggedGoGetsPage] Error fetching GoGets:', error);
      setReports([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filter, viewMode ]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchGoGets();
  }, [currentPage, filter, viewMode, fetchGoGets]);

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
    setCurrentPage(1);
  };

  const handleReviewComplete = () => {
    fetchGoGets();
    fetchStats();
    setSelectedGoGetId(null);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Flagged GoGets</h1>
          <p className="mt-1 text-sm text-gray-500">
            Review and moderate automatically and user-reported GoGet content
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={toggleViewMode}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            {viewMode === 'grid' ? (
              <>
                <List className="h-5 w-5" />
                <span>List View</span>
              </>
            ) : (
              <>
                <LayoutGrid className="h-5 w-5" />
                <span>Grid View</span>
              </>
            )}
          </button>
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg"
              value={filter.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
            >
              <option value="all">All Severity</option>
              <option value="high">High Severity</option>
              <option value="medium">Medium Severity</option>
              <option value="low">Low Severity</option>
            </select>
          </div>
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg"
            value={filter.source}
            onChange={(e) => handleFilterChange('source', e.target.value)}
          >
            <option value="all">All Sources</option>
            <option value="automated">Automated</option>
            <option value="user">User Reported</option>
          </select>
        </div>
      </div>

      {/* Stats Cards - Using unified component */}
      <ModerationStats stats={stats} contentType="goget" />

      {/* Content area */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-3 flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
            </div>
          ) : reports.length === 0 ? (
            <div className="col-span-3 text-center py-12 text-gray-500">
              No flagged GoGets found matching your criteria
            </div>
          ) : (
            reports.map(report => (
              <FlaggedGoGetCard
                key={report._id}
                report={report}
                onReview={() => setSelectedGoGetId(report._id)}
              />
            ))
          )}
        </div>
      ) : (
        <ModeratedContentList
          items={reports}
          loading={loading}
          contentType="goget"
          onReviewClick={setSelectedGoGetId}
        />
      )}
      
      {/* Pagination */}
      {!loading && reports.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex space-x-2">
            <button
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 disabled:opacity-50"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 disabled:opacity-50"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Review Modal - Using unified component */}
      <ReportReviewModal
        reportId={selectedGoGetId}
        isOpen={!!selectedGoGetId}
        onClose={() => setSelectedGoGetId(null)}
        onReviewComplete={handleReviewComplete}
        contentType="goget"
      />
    </div>
  );
}

export default FlaggedGoGetsPage;